/**
 * filterEmailsByType.ts
 * 
 * Utility function to filter customer emails by type ID
 * Returns matching email addresses and a boolean indicating if any were found
 */

import { Email } from '../integrations/allkort/graphql/interface/Customer';

export interface FilterEmailsResult {
	found: boolean;
	emails: string[];
}

/**
 * Filters emails by their type ID
 * 
 * @param emails - Array of email objects from CustomerContactData.currentCustomer.emails
 * @param typeId - The type ID to filter by (1=Information, 2=Faktura/Kontoutdrag, 3=Fordonsspecifikation, 4=Kvittokopia, 5=Orderinfo, 6=Prisbrev, 7=Huvudmail)
 * @returns Object with `found` boolean and `emails` array of matching email addresses
 * 
 * @example
 * const { found, emails } = filterEmailsByType(customerContactData.currentCustomer.emails, 3);
 * if (found) {
 *   console.log('Fordonsspecifikation emails:', emails);
 * }
 */
export const filterEmailsByType = (
	emails: Email[] | undefined,
	typeId: number
): FilterEmailsResult => {
	// Handle undefined or empty arrays
	if (!emails || emails.length === 0) {
		return { found: false, emails: [] };
	}

	// Filter emails by type ID and extract addresses
	const matchingEmails = emails
		.filter(email => email.type.id === typeId)
		.map(email => email.address);

	return {
		found: matchingEmails.length > 0,
		emails: matchingEmails
	};
};

/**
 * Email type constants for easy reference
 */
export const EMAIL_TYPES = {
	INFORMATION: 1,
	INVOICE_STATEMENT: 2,
	VEHICLE_SPECIFICATION: 3,
	RECEIPT_COPY: 4,
	ORDER_INFO: 5,
	PRICE_LETTER: 6,
	MAIN_EMAIL: 7
} as const;
